import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import { mangaList } from '@/mocks/manga';

const categoryMap: Record<string, string> = {
  riman: '日漫',
  hanman: '韩漫',
  guoman: '国漫',
};

const statusMap: Record<string, string> = {
  '连载中': '连载中',
  '已完结': '已完结',
};

export default function MangaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const manga = useMemo(() => mangaList.find((m) => m.id === Number(id)), [id]);

  const relatedManga = useMemo(() => {
    if (!manga) return [];
    return mangaList
      .filter((m) => m.id !== manga.id && (m.category === manga.category || m.subCategory === manga.subCategory))
      .slice(0, 6);
  }, [manga]);

  const chapters = useMemo(() => {
    if (!manga) return [];
    const count = Math.min(manga.chapters, 20);
    return Array.from({ length: count }, (_, i) => ({
      num: manga.chapters - i,
      title: `第${manga.chapters - i}话`,
      date: new Date(manga.updateTime).toISOString().split('T')[0],
    }));
  }, [manga]);

  if (!manga) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500 text-lg">未找到该漫画</p>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 font-medium text-sm">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <section className="py-8 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
              <Link to={`/category/${manga.category}`} className="hover:text-primary-500 transition-colors whitespace-nowrap">
                {categoryMap[manga.category] || manga.category}
              </Link>
              <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
              <span className="text-foreground-950 font-medium truncate">{manga.title}</span>
            </nav>

            <div className="flex flex-col md:flex-row gap-8">
              <div className="w-full md:w-72 flex-shrink-0">
                <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                  <img
                    src={manga.cover}
                    alt={manga.title}
                    title={`${manga.title} - 樱花漫画详情`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-primary-100 text-primary-700 whitespace-nowrap">
                    {categoryMap[manga.category] || manga.category}
                  </span>
                  {manga.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 rounded-md text-xs font-medium bg-secondary-100 text-secondary-700 whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                  <span className={`px-2.5 py-1 rounded-md text-xs font-medium whitespace-nowrap ${
                    manga.status === '连载中' ? 'bg-accent-100 text-accent-700' : 'bg-foreground-100 text-foreground-600'
                  }`}>
                    {manga.status}
                  </span>
                </div>

                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                  {manga.title}
                </h1>

                <div className="flex items-center gap-4 mb-4 text-sm text-foreground-500 flex-wrap">
                  <span>作者：<strong className="text-foreground-800 font-medium">{manga.author}</strong></span>
                  <span>共 <strong className="text-foreground-800 font-medium">{manga.chapters}</strong> 话</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500 w-4 h-4 flex items-center justify-center"></i>
                    <strong className="text-accent-500 font-medium">{manga.rating}</strong> 分
                  </span>
                  <span>
                    <i className="ri-eye-line w-4 h-4 inline-flex items-center justify-center mr-0.5"></i>
                    {(manga.views / 10000).toFixed(0)} 万阅读
                  </span>
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  {manga.description}
                </p>

                <div className="flex flex-wrap items-center gap-3">
                  <button className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-full text-sm transition-colors duration-200 cursor-pointer whitespace-nowrap">
                    <i className="ri-play-circle-line w-4 h-4 inline-flex items-center justify-center mr-1.5"></i>
                    开始阅读
                  </button>
                  <button className="px-6 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 font-medium rounded-full text-sm transition-colors duration-200 cursor-pointer whitespace-nowrap">
                    <i className="ri-heart-line w-4 h-4 inline-flex items-center justify-center mr-1.5"></i>
                    收藏漫画
                  </button>
                  <button className="px-6 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 font-medium rounded-full text-sm transition-colors duration-200 cursor-pointer whitespace-nowrap">
                    <i className="ri-download-line w-4 h-4 inline-flex items-center justify-center mr-1.5"></i>
                    下载离线
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-10">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-1 h-5 bg-primary-500 rounded-full"></div>
                <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">章节列表</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {chapters.map((ch) => (
                  <button
                    key={ch.num}
                    className="flex items-center justify-between p-3 rounded-lg bg-background-100 hover:bg-background-200 transition-colors duration-200 cursor-pointer text-left"
                  >
                    <span className="text-sm text-foreground-700 font-medium truncate">{ch.title}</span>
                    <span className="text-xs text-foreground-400 whitespace-nowrap ml-2">{ch.date}</span>
                  </button>
                ))}
              </div>
              {manga.chapters > 20 && (
                <button className="mt-4 w-full py-3 rounded-lg bg-background-100 hover:bg-background-200 text-sm text-foreground-600 font-medium transition-colors duration-200 cursor-pointer">
                  查看更多章节（共 {manga.chapters} 话）
                </button>
              )}
            </div>

            {relatedManga.length > 0 && (
              <div className="mt-10">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-1 h-5 bg-accent-500 rounded-full"></div>
                  <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">相关推荐</h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-product-shop="">
                  {relatedManga.map((rm) => (
                    <Link key={rm.id} to={`/manga/${rm.id}`} className="group block">
                      <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-2.5 bg-background-200">
                        <img
                          src={rm.cover}
                          alt={rm.title}
                          title={`${rm.title} - 樱花漫画`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <h3 className="font-heading text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1">
                        {rm.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-0.5">{rm.author}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}